import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';
import { PRICING } from '../lib/stripe-products';

interface CartItem {
  id: string;
  name: string;
  price: number;
  description: string;
  billingInterval?: 'month' | 'year';
  yearlyDiscountApplied?: boolean;
  isGift?: boolean;
  stripe_price_id?: string; // Added for database-driven checkout
  customer?: {
    firstName: string;
    lastName: string;
    email: string;
    startDate?: Date;
  };
  giftRecipient?: {
    firstName: string;
    lastName: string;
    email: string;
    message?: string;
  };
}

interface CartStore {
  items: CartItem[];
  lastCartActivity: number;
  addItem: (item: CartItem) => void;
  removeItem: (id: string) => void;
  updateItemInterval: (id: string, interval: 'month' | 'year') => void;
  toggleGiftStatus: (id: string, isGift: boolean) => void;
  updateGiftRecipient: (id: string, giftRecipient: CartItem['giftRecipient']) => void;
  clearCart: () => void;
  total: number;
}

export const useCartStore = create<CartStore>()(
  devtools(
    persist(
      (set) => ({
  items: [],
  total: 0,
  lastCartActivity: 0,
  addItem: (item) => set((state) => {
    // Set default billing interval to year if not specified
    // For subscription products, we assume the price is already the yearly price with discount applied
    let price = item.price;
    const billingInterval = item.billingInterval || 'year';
    
    // For yearly billing, we assume the price is already the yearly price with discount
    // No need to apply additional calculations here as we're passing the correct price from the component
    
    const newItem = {
      ...item,
      billingInterval,
      price,
      yearlyDiscountApplied: billingInterval === 'year' ? true : false
    };
    
    const newItems = [...state.items, newItem];
    return {
      items: newItems,
      total: newItems.reduce((sum, item) => sum + item.price, 0),
      lastCartActivity: Date.now()
    };
  }),
  updateItemInterval: (id, interval) => set((state) => {
    const newItems = state.items.map(item => {
      if (item.id !== id) return item;
      if (item.billingInterval === interval) return item;

      // Find the product in PRICING by matching name
      const pricingEntry = Object.values(PRICING).find(p =>
        item.name.includes(p.name) || p.name.includes(item.name.split(' - ')[0])
      );

      if (pricingEntry && 'monthly' in pricingEntry) {
        const newPrice = interval === 'year' ? pricingEntry.yearly : pricingEntry.monthly;
        const newPriceId = interval === 'year' ? pricingEntry.yearlyPriceId : pricingEntry.monthlyPriceId;
        return {
          ...item,
          billingInterval: interval,
          price: newPrice,
          stripe_price_id: newPriceId,
          yearlyDiscountApplied: interval === 'year',
        };
      }

      // Fallback for one-time products (shouldn't switch intervals)
      return item;
    });
    return {
      items: newItems,
      total: newItems.reduce((sum, i) => sum + i.price, 0),
      lastCartActivity: Date.now(),
    };
  }),
  removeItem: (id) => set((state) => {
    const newItems = state.items.filter(item => item.id !== id);
    return {
      items: newItems,
      total: newItems.reduce((sum, item) => sum + item.price, 0)
    };
  }),
  clearCart: () => set({ items: [], total: 0 }),
  
  toggleGiftStatus: (id, isGift) => set((state) => {
    const newItems = state.items.map(item => {
      if (item.id === id) {
        return { ...item, isGift };
      }
      return item;
    });
    
    return {
      items: newItems,
      total: newItems.reduce((sum, item) => sum + item.price, 0),
      lastCartActivity: Date.now()
    };
  }),

  updateGiftRecipient: (id, giftRecipient) => set((state) => {
    const newItems = state.items.map(item => {
      if (item.id === id) {
        return { ...item, giftRecipient, isGift: true };
      }
      return item;
    });
    
    return {
      items: newItems,
      total: newItems.reduce((sum, item) => sum + item.price, 0),
      lastCartActivity: Date.now()
    };
  }),
      }),
      {
        name: 'jmefit-cart',
        partialize: (state) => ({ items: state.items, total: state.total, lastCartActivity: state.lastCartActivity }),
      }
    ),
    { name: 'CartStore' }
  )
);